import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useLocation } from "react-router-dom";
import { HsnSacSearch } from "@/components/HsnSacSearch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtINR, fmtNum } from "@/lib/format";
import { toast } from "sonner";
import { AlertTriangle, Plus, Trash2, ChevronRight, ChevronDown, Package, Warehouse as WhIcon, MapPin, Download, Car } from "lucide-react";
import { exportXlsx } from "@/lib/xlsx-export";
import { useAuth } from "@/context/AuthContext";

const BASE_ITEM_TYPES = [
  { v: "raw_material", l: "Raw Material" }, { v: "wip", l: "WIP" },
  { v: "finished_good", l: "Finished Good" }, { v: "consumable", l: "Consumable" },
  { v: "spare", l: "Spare" }, { v: "tool", l: "Tool" },
  { v: "service", l: "Service" }, { v: "goods", l: "Goods (generic)" },
];
// Iter-151 · Dealership-specific categories — appended when the org has the
// Dealership 360 add-on active. Keeps the picker tenant-appropriate.
const DEALERSHIP_ITEM_TYPES = [
  { v: "vehicle",            l: "🚗 Vehicle" },
  { v: "accessory",          l: "🔧 Accessory" },
  { v: "extended_warranty",  l: "🛡 Extended Warranty" },
  { v: "part",               l: "⚙️ Part" },
];
const WH_TYPES = ["storage", "production", "scrap", "quality", "transit", "subcontract"];
const ZONE_TYPES = ["storage", "qc_hold", "scrap", "receiving", "dispatch"];

// -------------------- Items --------------------
function ItemsTab({ servicesOnly = false }) {
  const { org } = useAuth();
  // Iter-151 · Tenant-appropriate item-type picker. Dealership orgs see 4 extra
  // categories (vehicle / accessory / extended_warranty / part) at the top.
  const ITEM_TYPES = useMemo(
    () => (org?.is_dealership ? [...DEALERSHIP_ITEM_TYPES, ...BASE_ITEM_TYPES] : BASE_ITEM_TYPES),
    [org?.is_dealership]
  );
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = {
    sku: "", name: "", category: "", unit: servicesOnly ? "NOS" : "PCS", hsn_code: "", gst_rate: 18,
    purchase_price: 0, sale_price: 0, reorder_level: 0, is_service: !!servicesOnly,
    // v2
    item_type: servicesOnly ? "service" : "goods", stocking_uom: "", purchase_uom: "", sales_uom: "",
    safety_stock: 0, min_stock: 0, max_stock: 0, lead_time_days: 0,
    default_warehouse_id: "", is_batch_tracked: false, is_serial_tracked: false,
    shelf_life_days: 0, quality_check_required: false,
    // Iter-103 · FEFO for perishables, FIFO for durable / non-perishable
    picking_method: "fefo",
    uom_conversions: [],
    // Feb 2026: item-level default posting accounts
    default_income_account_id: "", default_purchase_account_id: "",
    // Iter-151 · Vehicle-specific sub-object (surfaced when item_type=vehicle)
    vehicle_details: null,
  };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setItems((await api.get("/items")).data);
    setWarehouses((await api.get("/warehouses")).data);
    setAccounts((await api.get("/accounts")).data);
  };
  useEffect(() => { load(); }, []);

  const incomeOpts   = accounts.filter(a => a.account_type === "income" && a.is_active !== false);
  const expenseOpts  = accounts.filter(a => a.account_type === "expense" && a.is_active !== false);

  const save = async () => {
    if (!form.sku || !form.name) return toast.error("SKU and Name required");
    const payload = { ...form,
      default_warehouse_id: form.default_warehouse_id || null,
      default_income_account_id:   (form.default_income_account_id   && form.default_income_account_id   !== "__none__") ? form.default_income_account_id   : null,
      default_purchase_account_id: (form.default_purchase_account_id && form.default_purchase_account_id !== "__none__") ? form.default_purchase_account_id : null,
    };
    try {
      if (edit) await api.put(`/items/${edit.id}`, payload);
      else await api.post("/items", payload);
      toast.success("Saved"); setOpen(false); setEdit(null); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete item?")) return;
    try { await api.delete(`/items/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addConv = () => setForm({ ...form, uom_conversions: [...form.uom_conversions, { from_uom: "", to_uom: "", factor: 1 }] });
  const removeConv = (i) => setForm({ ...form, uom_conversions: form.uom_conversions.filter((_, j) => j !== i) });

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => { setEdit(null); setForm(empty); setOpen(true); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-item-btn"><Plus className="w-3.5 h-3.5" /> Item</Button>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>SKU</th><th>Name</th><th>Type</th><th>Category</th><th>HSN</th><th className="text-right">GST</th><th className="text-right">Cost</th><th className="text-right">Sale</th><th className="text-right">Stock</th><th className="text-right">Reorder</th><th className="text-right">Safety</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="12" className="text-center text-muted-foreground py-8">No items yet</td></tr>}
            {items.map((i) => (
              <tr key={i.id} data-testid={`item-row-${i.sku}`}>
                <td className="font-mono text-xs">{i.sku}</td>
                <td className="font-medium">{i.name}</td>
                <td><span className="text-[10px] font-mono uppercase text-accent">{i.item_type || "goods"}</span></td>
                <td className="text-xs text-muted-foreground">{i.category || "—"}</td>
                <td className="font-mono text-xs">{i.hsn_code || "—"}</td>
                <td className="text-right text-xs">{i.gst_rate}%</td>
                <td className="text-right tabular-nums">{fmtINR(i.purchase_price)}</td>
                <td className="text-right tabular-nums">{fmtINR(i.sale_price)}</td>
                <td className="text-right tabular-nums font-medium">
                  {fmtNum(i.stock_on_hand)} {i.stock_on_hand <= i.reorder_level && i.reorder_level > 0 && <AlertTriangle className="w-3.5 h-3.5 text-warning inline ml-1" />}
                </td>
                <td className="text-right tabular-nums text-xs text-muted-foreground">{fmtNum(i.reorder_level)}</td>
                <td className="text-right tabular-nums text-xs text-muted-foreground">{fmtNum(i.safety_stock || 0)}</td>
                <td className="text-right">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setEdit(i); setForm({ ...empty, ...i, default_warehouse_id: i.default_warehouse_id || "", uom_conversions: i.uom_conversions || [] }); setOpen(true); }} data-testid={`edit-item-${i.sku}`}>Edit</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => del(i.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none space-y-3 overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading">{edit ? "Edit" : "New"} Item</SheetTitle></SheetHeader>

          <div className="grid grid-cols-3 gap-3">
            <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="rounded-sm mt-1 font-mono" data-testid="item-sku" /></div>
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" data-testid="item-name" /></div>
            <div><Label>Item type</Label>
              <Select value={form.item_type} onValueChange={(v) => setForm({ ...form, item_type: v,
                // If flipping into vehicle, seed an empty vehicle_details block.
                vehicle_details: v === "vehicle" ? (form.vehicle_details || {}) : null,
                // Vehicles are always serial-tracked (one VIN = one unit).
                is_serial_tracked: v === "vehicle" ? true : form.is_serial_tracked,
              })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="item-type"><SelectValue /></SelectTrigger>
                <SelectContent>{ITEM_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Category</Label><Input value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>Base UOM</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>Stocking UOM</Label><Input value={form.stocking_uom || ""} onChange={(e) => setForm({ ...form, stocking_uom: e.target.value })} className="rounded-sm mt-1" placeholder={form.unit} /></div>
            <div><Label>Purchase UOM</Label><Input value={form.purchase_uom || ""} onChange={(e) => setForm({ ...form, purchase_uom: e.target.value })} className="rounded-sm mt-1" placeholder={form.unit} /></div>
            <div><Label>Sales UOM</Label><Input value={form.sales_uom || ""} onChange={(e) => setForm({ ...form, sales_uom: e.target.value })} className="rounded-sm mt-1" placeholder={form.unit} /></div>
            <div><Label>{form.is_service ? "SAC" : "HSN"}</Label><div className="mt-1"><HsnSacSearch value={form.hsn_code} kind={form.is_service ? "sac" : "hsn"} testId="item-hsn"
              onChange={(v) => setForm((p) => ({ ...p, hsn_code: v }))}
              onPick={(r) => setForm((p) => ({ ...p, hsn_code: r.code, gst_rate: r.gst_rate ?? p.gst_rate }))} /></div></div>
            <div><Label>GST %</Label><Input type="number" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>Purchase ₹</Label><Input type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>Sale ₹</Label><Input type="number" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            {!form.is_service && (<>
            <div><Label>Lead time (d)</Label><Input type="number" value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>Reorder level</Label><Input type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" data-testid="item-reorder" /></div>
            <div><Label>Safety stock</Label><Input type="number" value={form.safety_stock} onChange={(e) => setForm({ ...form, safety_stock: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" data-testid="item-safety" /></div>
            <div><Label>Min stock</Label><Input type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>Max stock</Label><Input type="number" value={form.max_stock} onChange={(e) => setForm({ ...form, max_stock: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>Shelf life (d)</Label><Input type="number" value={form.shelf_life_days} onChange={(e) => setForm({ ...form, shelf_life_days: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div className="col-span-3"><Label>Default warehouse</Label>
              <Select value={form.default_warehouse_id || ""} onValueChange={(v) => setForm({ ...form, default_warehouse_id: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="(none)" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            </>)}
          </div>

          {/* Iter-151 · Vehicle-specific sub-form — only when item_type=vehicle */}
          {form.item_type === "vehicle" && (
            <div className="border rounded-sm p-3 bg-sky-50/40 border-sky-200 space-y-3" data-testid="item-vehicle-details">
              <div className="text-[10px] font-mono uppercase tracking-widest text-sky-700 flex items-center gap-1.5">
                <Car className="w-3 h-3" /> Vehicle-specific details
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>VIN *</Label>
                  <Input value={form.vehicle_details?.vin || ""} maxLength={17}
                    onChange={(e) => setForm({ ...form, vehicle_details: { ...(form.vehicle_details || {}), vin: e.target.value.toUpperCase() } })}
                    className="rounded-sm mt-1 font-mono text-[11px]" placeholder="17-char VIN" data-testid="veh-vin" />
                </div>
                <div>
                  <Label>Chassis #</Label>
                  <Input value={form.vehicle_details?.chassis_number || ""}
                    onChange={(e) => setForm({ ...form, vehicle_details: { ...(form.vehicle_details || {}), chassis_number: e.target.value.toUpperCase() } })}
                    className="rounded-sm mt-1 font-mono text-[11px]" data-testid="veh-chassis" />
                </div>
                <div>
                  <Label>Engine #</Label>
                  <Input value={form.vehicle_details?.engine_number || ""}
                    onChange={(e) => setForm({ ...form, vehicle_details: { ...(form.vehicle_details || {}), engine_number: e.target.value.toUpperCase() } })}
                    className="rounded-sm mt-1 font-mono text-[11px]" data-testid="veh-engine" />
                </div>
                <div>
                  <Label>Variant</Label>
                  <Input value={form.vehicle_details?.variant || ""}
                    onChange={(e) => setForm({ ...form, vehicle_details: { ...(form.vehicle_details || {}), variant: e.target.value } })}
                    className="rounded-sm mt-1" placeholder="e.g. XZ+ LUX" />
                </div>
                <div>
                  <Label>Body type</Label>
                  <Select value={form.vehicle_details?.body_type || ""}
                    onValueChange={(v) => setForm({ ...form, vehicle_details: { ...(form.vehicle_details || {}), body_type: v } })}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Pick" /></SelectTrigger>
                    <SelectContent>
                      {["Hatchback","Sedan","SUV","Compact SUV","MPV","Coupe","Convertible","Pickup","Van","Truck","Bus","Two-wheeler","Three-wheeler"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Colour</Label>
                  <Input value={form.vehicle_details?.colour || ""}
                    onChange={(e) => setForm({ ...form, vehicle_details: { ...(form.vehicle_details || {}), colour: e.target.value } })}
                    className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label>Fuel type</Label>
                  <Select value={form.vehicle_details?.fuel_type || ""}
                    onValueChange={(v) => setForm({ ...form, vehicle_details: { ...(form.vehicle_details || {}), fuel_type: v } })}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Pick" /></SelectTrigger>
                    <SelectContent>
                      {["Petrol","Diesel","CNG","Electric","Hybrid","Plug-in Hybrid","LPG"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Transmission</Label>
                  <Select value={form.vehicle_details?.transmission || ""}
                    onValueChange={(v) => setForm({ ...form, vehicle_details: { ...(form.vehicle_details || {}), transmission: v } })}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Pick" /></SelectTrigger>
                    <SelectContent>
                      {["Manual","Automatic","AMT","CVT","DCT","IVT"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Drive type</Label>
                  <Select value={form.vehicle_details?.drive_type || ""}
                    onValueChange={(v) => setForm({ ...form, vehicle_details: { ...(form.vehicle_details || {}), drive_type: v } })}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Pick" /></SelectTrigger>
                    <SelectContent>
                      {["FWD","RWD","AWD","4WD"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Year of manufacture</Label>
                  <Input type="number" value={form.vehicle_details?.year_of_manufacture || ""}
                    onChange={(e) => setForm({ ...form, vehicle_details: { ...(form.vehicle_details || {}), year_of_manufacture: Number(e.target.value) } })}
                    className="rounded-sm mt-1 font-mono" placeholder="e.g. 2026" />
                </div>
                <div>
                  <Label>Month of manufacture</Label>
                  <Input type="number" min="1" max="12" value={form.vehicle_details?.month_of_manufacture || ""}
                    onChange={(e) => setForm({ ...form, vehicle_details: { ...(form.vehicle_details || {}), month_of_manufacture: Number(e.target.value) } })}
                    className="rounded-sm mt-1 font-mono" placeholder="1-12" />
                </div>
                <div>
                  <Label>OEM invoice #</Label>
                  <Input value={form.vehicle_details?.oem_invoice_number || ""}
                    onChange={(e) => setForm({ ...form, vehicle_details: { ...(form.vehicle_details || {}), oem_invoice_number: e.target.value } })}
                    className="rounded-sm mt-1 font-mono text-[11px]" />
                </div>
                <div>
                  <Label>OEM invoice value ₹</Label>
                  <Input type="number" value={form.vehicle_details?.oem_invoice_value || 0}
                    onChange={(e) => setForm({ ...form, vehicle_details: { ...(form.vehicle_details || {}), oem_invoice_value: Number(e.target.value) } })}
                    className="rounded-sm mt-1 font-mono text-right" />
                </div>
                <div>
                  <Label>Ex-showroom price ₹</Label>
                  <Input type="number" value={form.vehicle_details?.ex_showroom_price || form.sale_price || 0}
                    onChange={(e) => setForm({ ...form, vehicle_details: { ...(form.vehicle_details || {}), ex_showroom_price: Number(e.target.value) }, sale_price: Number(e.target.value) })}
                    className="rounded-sm mt-1 font-mono text-right" data-testid="veh-exshow" />
                </div>
              </div>
              <div className="text-[10px] text-muted-foreground italic">
                Vehicle-Stock module in Dealership 360 reads from these fields. Serial-tracking is enforced (one VIN = one unit).
              </div>
            </div>
          )}

          {/* Feb 2026 — Item-level default posting accounts */}
          <div className="border rounded-sm p-3 bg-accent/5">
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-2">
              // Default posting accounts
            </div>
            <div className="text-[11px] text-muted-foreground mb-3">
              Each sales invoice / purchase bill line will post to these ledgers automatically. GST accounts are always auto-derived from Place-of-Supply.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Default Income Account <span className="text-[9px] text-muted-foreground">(Sales side)</span></Label>
                <Select value={form.default_income_account_id || ""}
                          onValueChange={(v) => setForm({ ...form, default_income_account_id: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="item-default-income-account">
                    <SelectValue placeholder="Auto — Sales" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">Auto — pick generic Sales</SelectItem>
                    {incomeOpts.map(a => (
                      <SelectItem key={a.id} value={a.id} data-testid={`item-income-opt-${a.code}`}>
                        <span className="font-mono text-[10px]">{a.code}</span> · {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Default Purchase Account <span className="text-[9px] text-muted-foreground">(Bill side)</span></Label>
                <Select value={form.default_purchase_account_id || ""}
                          onValueChange={(v) => setForm({ ...form, default_purchase_account_id: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="item-default-purchase-account">
                    <SelectValue placeholder="Auto — Purchase" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">Auto — pick generic Purchase</SelectItem>
                    {expenseOpts.map(a => (
                      <SelectItem key={a.id} value={a.id} data-testid={`item-purchase-opt-${a.code}`}>
                        <span className="font-mono text-[10px]">{a.code}</span> · {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* UOM conversions */}
          <div className="border rounded-sm">
            <div className="px-3 py-2 border-b flex items-center justify-between">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">UOM conversions</div>
              <Button variant="ghost" size="sm" onClick={addConv} className="rounded-sm gap-1 h-7"><Plus className="w-3.5 h-3.5" /> Row</Button>
            </div>
            {form.uom_conversions.length === 0 && <div className="p-3 text-xs text-muted-foreground">e.g. 1 BOX = 12 PCS · 1 KG = 1000 GM</div>}
            {form.uom_conversions.map((c, i) => (
              <div key={i} className="p-2 grid grid-cols-12 gap-2 items-center border-b border-border/40">
                <span className="col-span-1 text-xs">1</span>
                <Input value={c.from_uom} onChange={(e) => setForm({ ...form, uom_conversions: form.uom_conversions.map((x, j) => j === i ? { ...x, from_uom: e.target.value } : x) })} className="col-span-3 rounded-sm h-8 text-xs" placeholder="BOX" />
                <span className="col-span-1 text-xs text-center">=</span>
                <Input type="number" value={c.factor} onChange={(e) => setForm({ ...form, uom_conversions: form.uom_conversions.map((x, j) => j === i ? { ...x, factor: Number(e.target.value) } : x) })} className="col-span-3 rounded-sm h-8 text-xs text-right tabular-nums" />
                <Input value={c.to_uom} onChange={(e) => setForm({ ...form, uom_conversions: form.uom_conversions.map((x, j) => j === i ? { ...x, to_uom: e.target.value } : x) })} className="col-span-3 rounded-sm h-8 text-xs" placeholder="PCS" />
                <button className="col-span-1" onClick={() => removeConv(i)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center justify-between"><Label>Batch tracked</Label><Switch checked={form.is_batch_tracked} onCheckedChange={(v) => setForm({ ...form, is_batch_tracked: v })} /></div>
            <div className="flex items-center justify-between"><Label>Serial tracked</Label><Switch checked={form.is_serial_tracked} onCheckedChange={(v) => setForm({ ...form, is_serial_tracked: v })} /></div>
            <div className="flex items-center justify-between"><Label>QC required</Label><Switch checked={form.quality_check_required} onCheckedChange={(v) => setForm({ ...form, quality_check_required: v })} /></div>
            <div className="flex items-center justify-between"><Label>Service (no stock)</Label><Switch checked={form.is_service} onCheckedChange={(v) => setForm({ ...form, is_service: v })} /></div>
          </div>

          {/* Iter-103 · Picking method FEFO/FIFO per item */}
          <div>
            <Label className="text-[10px] uppercase font-mono tracking-widest">Picking method</Label>
            <Select value={form.picking_method || "fefo"} onValueChange={(v) => setForm({ ...form, picking_method: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="item-picking-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fefo">FEFO — First Expiry First Out (perishables)</SelectItem>
                <SelectItem value="fifo">FIFO — First In First Out (durables)</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-[10px] text-muted-foreground mt-1 font-mono">FEFO requires exp_date on batches · FIFO falls back to inward date.</div>
          </div>

          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="item-save">Save</Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// -------------------- Warehouse tree --------------------
function WarehouseTree() {
  const [tree, setTree] = useState([]);
  const [expandedW, setExpandedW] = useState({});
  const [expandedZ, setExpandedZ] = useState({});
  const [whOpen, setWhOpen] = useState(false);
  const [zoneOpen, setZoneOpen] = useState(null);   // warehouse
  const [binOpen, setBinOpen] = useState(null);     // {warehouse_id, zone_id}
  const [wForm, setWForm] = useState({ code: "", name: "", location: "", address: "", warehouse_type: "storage" });
  const [zForm, setZForm] = useState({ code: "", name: "", zone_type: "storage" });
  const [bForm, setBForm] = useState({ code: "", name: "", capacity: 0 });

  const load = async () => setTree((await api.get("/warehouses/tree")).data);
  useEffect(() => { load(); }, []);

  const createWh = async () => {
    if (!wForm.code || !wForm.name) return toast.error("Code + Name required");
    try { await api.post("/warehouses", wForm); toast.success("Warehouse added"); setWhOpen(false); setWForm({ code: "", name: "", location: "", address: "", warehouse_type: "storage" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const createZone = async () => {
    if (!zForm.code || !zForm.name) return toast.error("Code + Name required");
    try { await api.post("/warehouse-zones", { ...zForm, warehouse_id: zoneOpen.id }); toast.success("Zone added"); setZoneOpen(null); setZForm({ code: "", name: "", zone_type: "storage" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const createBin = async () => {
    if (!bForm.code) return toast.error("Code required");
    try { await api.post("/warehouse-bins", { ...bForm, warehouse_id: binOpen.warehouse_id, zone_id: binOpen.zone_id }); toast.success("Bin added"); setBinOpen(null); setBForm({ code: "", name: "", capacity: 0 }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delWh = async (id) => { if (!window.confirm("Delete warehouse + all zones + bins?")) return; try { await api.delete(`/warehouses/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const delZone = async (id) => { if (!window.confirm("Delete zone?")) return; try { await api.delete(`/warehouse-zones/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const delBin = async (id) => { try { await api.delete(`/warehouse-bins/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  return (
    <div className="space-y-3" data-testid="warehouse-tree">
      <div className="flex justify-end">
        <Button onClick={() => setWhOpen(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-warehouse-btn"><Plus className="w-3.5 h-3.5" /> Warehouse</Button>
      </div>
      <div className="card-sharp p-2">
        {tree.length === 0 && <div className="text-center text-muted-foreground py-10">No warehouses yet</div>}
        {tree.map((w) => (
          <div key={w.id} className="border-b border-border/40 last:border-0">
            <div className="flex items-center gap-2 px-2 py-2 hover:bg-accent/5">
              <button onClick={() => setExpandedW({ ...expandedW, [w.id]: !expandedW[w.id] })}>{expandedW[w.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>
              <WhIcon className="w-4 h-4 text-accent" />
              <span className="font-medium flex-1" data-testid={`wh-${w.code}`}>{w.name}</span>
              <span className="font-mono text-xs text-muted-foreground">{w.code}</span>
              <span className="text-[10px] font-mono uppercase text-accent">{w.warehouse_type || "storage"}</span>
              <span className="text-xs text-muted-foreground">{w.zones?.length || 0} zones</span>
              <Button size="sm" variant="ghost" className="h-6 text-xs gap-1" onClick={() => setZoneOpen(w)}><Plus className="w-3 h-3" /> Zone</Button>
              <Button size="sm" variant="ghost" className="h-6 text-red-500" onClick={() => delWh(w.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
            {expandedW[w.id] && (w.zones || []).map((z) => (
              <div key={z.id} className="ml-6 border-l-2 border-accent/20 pl-3">
                <div className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent/5">
                  <button onClick={() => setExpandedZ({ ...expandedZ, [z.id]: !expandedZ[z.id] })}>{expandedZ[z.id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</button>
                  <Package className="w-3.5 h-3.5 text-yellow-500" />
                  <span className="text-sm flex-1">{z.name}</span>
                  <span className="font-mono text-xs text-muted-foreground">{z.code}</span>
                  <span className="text-[10px] font-mono uppercase text-yellow-600">{z.zone_type}</span>
                  <span className="text-xs text-muted-foreground">{z.bins?.length || 0} bins</span>
                  <Button size="sm" variant="ghost" className="h-6 text-xs gap-1" onClick={() => setBinOpen({ warehouse_id: w.id, zone_id: z.id })}><Plus className="w-3 h-3" /> Bin</Button>
                  <Button size="sm" variant="ghost" className="h-6 text-red-500" onClick={() => delZone(z.id)}><Trash2 className="w-3 h-3" /></Button>
                </div>
                {expandedZ[z.id] && (z.bins || []).map((b) => (
                  <div key={b.id} className="ml-6 flex items-center gap-2 px-2 py-1 text-xs hover:bg-accent/5">
                    <MapPin className="w-3 h-3 text-blue-500" />
                    <span className="font-mono flex-1">{b.code}</span>
                    <span className="text-muted-foreground">{b.name || ""}</span>
                    {b.capacity > 0 && <span className="text-muted-foreground">cap {b.capacity}</span>}
                    <button className="text-red-500" onClick={() => delBin(b.id)}><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Warehouse dialog */}
      <Sheet open={whOpen} onOpenChange={setWhOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader><SheetTitle className="font-heading">New Warehouse</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input value={wForm.code} onChange={(e) => setWForm({ ...wForm, code: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" data-testid="wh-code" /></div>
            <div><Label>Type</Label>
              <Select value={wForm.warehouse_type} onValueChange={(v) => setWForm({ ...wForm, warehouse_type: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{WH_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Name</Label><Input value={wForm.name} onChange={(e) => setWForm({ ...wForm, name: e.target.value })} className="rounded-sm mt-1" data-testid="wh-name" /></div>
            <div className="col-span-2"><Label>Location</Label><Input value={wForm.location} onChange={(e) => setWForm({ ...wForm, location: e.target.value })} className="rounded-sm mt-1" /></div>
            <div className="col-span-2"><Label>Address</Label><Input value={wForm.address} onChange={(e) => setWForm({ ...wForm, address: e.target.value })} className="rounded-sm mt-1" /></div>
          </div>
          <Button onClick={createWh} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="wh-save">Save</Button>
        </SheetContent>
      </Sheet>

      {/* Zone dialog */}
      <Sheet open={!!zoneOpen} onOpenChange={(o) => !o && setZoneOpen(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader><SheetTitle className="font-heading">New Zone in {zoneOpen?.name}</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input value={zForm.code} onChange={(e) => setZForm({ ...zForm, code: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" data-testid="zone-code" /></div>
            <div><Label>Type</Label>
              <Select value={zForm.zone_type} onValueChange={(v) => setZForm({ ...zForm, zone_type: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{ZONE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Name</Label><Input value={zForm.name} onChange={(e) => setZForm({ ...zForm, name: e.target.value })} className="rounded-sm mt-1" data-testid="zone-name" /></div>
          </div>
          <Button onClick={createZone} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="zone-save">Save</Button>
        </SheetContent>
      </Sheet>

      {/* Bin dialog */}
      <Sheet open={!!binOpen} onOpenChange={(o) => !o && setBinOpen(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader><SheetTitle className="font-heading">New Bin</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input value={bForm.code} onChange={(e) => setBForm({ ...bForm, code: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" data-testid="bin-code" /></div>
            <div><Label>Capacity</Label><Input type="number" value={bForm.capacity} onChange={(e) => setBForm({ ...bForm, capacity: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div className="col-span-2"><Label>Name (optional)</Label><Input value={bForm.name} onChange={(e) => setBForm({ ...bForm, name: e.target.value })} className="rounded-sm mt-1" /></div>
          </div>
          <Button onClick={createBin} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="bin-save">Save</Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function MovementsTab() {
  const [movements, setMovements] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [view, setView] = useState(null);
  useEffect(() => { (async () => {
    setMovements((await api.get("/stock/movements")).data);
    setItems((await api.get("/items")).data);
    setWarehouses((await api.get("/warehouses")).data);
  })(); }, []);
  const itemOf = (id) => items.find((i) => i.id === id);
  const whOf   = (id) => warehouses.find((w) => w.id === id);
  return (
    <>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Date</th><th>Item</th><th>Warehouse</th><th>Type</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Value</th><th>Reference</th></tr></thead>
          <tbody>
            {movements.length === 0 && <tr><td colSpan="8" className="text-center text-muted-foreground py-8">No movements</td></tr>}
            {movements.map((m) => (
              <tr key={m.id} onClick={() => setView(m)} className="cursor-pointer hover:bg-muted/40" data-testid={`mov-row-${m.id.slice(0,8)}`}>
                <td className="text-xs font-mono">{m.date}</td>
                <td className="text-xs">{itemOf(m.item_id)?.name || "—"}<div className="text-[10px] font-mono text-muted-foreground">{itemOf(m.item_id)?.sku}</div></td>
                <td className="text-xs">{whOf(m.warehouse_id)?.code || "—"}</td>
                <td><span className={`text-[10px] font-mono uppercase ${m.movement_type === "inward" ? "text-emerald-600" : "text-accent"}`}>{m.movement_type}</span></td>
                <td className="text-right tabular-nums">{fmtNum(m.qty)}</td>
                <td className="text-right tabular-nums text-muted-foreground">{m.rate ? fmtINR(m.rate) : "—"}</td>
                <td className="text-right tabular-nums">{fmtINR(m.value)}</td>
                <td className="text-xs font-mono text-muted-foreground">{m.reference || m.reference_type || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Sheet open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl rounded-none space-y-4 overflow-y-auto" data-testid="mov-view-drawer">
          <SheetHeader className="pb-3 border-b">
            <SheetTitle className="font-heading flex items-center gap-3">
              <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-sm ${view?.movement_type === "inward" ? "bg-emerald-500/10 text-emerald-600" : "bg-blue-500/10 text-blue-600"}`}>{view?.movement_type}</span>
              Stock Movement · {view?.date}
            </SheetTitle>
          </SheetHeader>
          {view && (
            <div className="text-sm space-y-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 border rounded-sm p-4 bg-muted/20">
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Item</div><div className="mt-1">{itemOf(view.item_id)?.name || "—"}</div><div className="text-[10px] font-mono text-muted-foreground">{itemOf(view.item_id)?.sku}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Warehouse</div><div className="mt-1 font-mono">{whOf(view.warehouse_id)?.code || "—"}</div><div className="text-[10px] text-muted-foreground">{whOf(view.warehouse_id)?.name}</div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Qty</div><div className="mt-1 text-lg tabular-nums font-bold">{fmtNum(view.qty)} <span className="text-xs text-muted-foreground">{itemOf(view.item_id)?.unit || ""}</span></div></div>
                <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Rate</div><div className="mt-1 text-lg tabular-nums">{view.rate ? fmtINR(view.rate) : "—"}</div></div>
                <div className="col-span-2 border-t pt-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Total value</div><div className="mt-1 text-2xl tabular-nums font-bold text-accent">{fmtINR(view.value || 0)}</div></div>
              </div>
              {(view.reference_type || view.reference) && (
                <div className="border rounded-sm p-3 bg-accent/5">
                  <div className="text-[10px] font-mono uppercase text-accent">// Source document</div>
                  <div className="mt-1 text-sm font-mono">{view.reference_type} · {view.reference || view.reference_id?.slice(0, 8) || "—"}</div>
                </div>
              )}
              {view.narration && <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Narration</div><div className="mt-1 text-xs">{view.narration}</div></div>}
              <div className="text-[10px] font-mono text-muted-foreground">Movement id: {view.id}</div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

function TransfersTab() {
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [form, setForm] = useState({ item_id: "", qty: 1, from_warehouse_id: "", to_warehouse_id: "", transfer_date: new Date().toISOString().slice(0, 10), narration: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { (async () => {
    setItems((await api.get("/items")).data);
    setWarehouses((await api.get("/warehouses")).data);
  })(); }, []);
  const submit = async () => {
    if (!form.item_id) return toast.error("Select an item");
    if (!form.from_warehouse_id || !form.to_warehouse_id) return toast.error("Pick both warehouses");
    if (form.from_warehouse_id === form.to_warehouse_id) return toast.error("Source and destination must differ");
    setBusy(true);
    try {
      const r = await api.post("/stock/transfer", form);
      toast.success(`Transfer posted · ${r.data.reference}`);
      setForm({ ...form, qty: 1, narration: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="card-sharp p-5 space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Inter-warehouse Transfer</div>
        <div><Label>Item</Label>
          <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="xfer-item"><SelectValue placeholder="Select item" /></SelectTrigger>
            <SelectContent className="max-h-72">{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.sku} · {i.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>From warehouse</Label>
            <Select value={form.from_warehouse_id} onValueChange={(v) => setForm({ ...form, from_warehouse_id: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="xfer-from"><SelectValue placeholder="Source" /></SelectTrigger>
              <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>To warehouse</Label>
            <Select value={form.to_warehouse_id} onValueChange={(v) => setForm({ ...form, to_warehouse_id: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="xfer-to"><SelectValue placeholder="Destination" /></SelectTrigger>
              <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Qty</Label><Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: Number(e.target.value) })} className="rounded-sm mt-1 text-right tabular-nums" data-testid="xfer-qty" /></div>
          <div><Label>Date</Label><Input type="date" value={form.transfer_date} onChange={(e) => setForm({ ...form, transfer_date: e.target.value })} className="rounded-sm mt-1" data-testid="xfer-date" /></div>
        </div>
        <div><Label>Narration</Label><Input value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} className="rounded-sm mt-1" data-testid="xfer-narration" /></div>
        <Button onClick={submit} disabled={busy} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="xfer-submit">{busy ? "Posting…" : "Post Transfer"}</Button>
      </div>
      <div className="card-sharp p-5 space-y-2 text-sm">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">How this works</div>
        <p className="text-muted-foreground">One transfer emits two <b>StockMovement</b> rows — an <b>outward</b> from the source warehouse and an <b>inward</b> to the destination — so per-warehouse stock summaries stay perfectly balanced. The transaction is also audit-logged with a unique reference like <span className="font-mono text-xs">XFER/202602/A4C1D2</span>.</p>
        <p className="text-muted-foreground">No GST is charged (same-org movement), but a Delivery Challan / E-way Bill can be printed downstream when moving stock across states.</p>
      </div>
    </div>
  );
}

function StockSummaryTab() {
  const [data, setData] = useState({ rows: [], valuation_method: "weighted_average", total_qty: 0, total_value: 0 });
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [wh, setWh] = useState("");
  const [item_id, setItem] = useState("");
  const [category, setCategory] = useState("");
  const [sku_q, setSkuQ] = useState("");
  const [from_date, setFrom] = useState("");
  const [to_date, setTo] = useState("");

  const load = async () => {
    const params = {};
    if (wh) params.warehouse_id = wh;
    if (item_id) params.item_id = item_id;
    if (category) params.category = category;
    if (sku_q) params.sku_q = sku_q;
    if (from_date) params.from_date = from_date;
    if (to_date) params.to_date = to_date;
    const r = await api.get("/stock/summary", { params });
    setData(Array.isArray(r.data) ? { rows: r.data, valuation_method: "weighted_average", total_qty: 0, total_value: 0 } : r.data);
  };
  useEffect(() => {
    api.get("/warehouses").then((r) => setWarehouses(r.data));
    api.get("/items").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wh, item_id, category, from_date, to_date]);

  const rows = data.rows || [];
  const categories = [...new Set(items.map((i) => i.category).filter(Boolean))];

  const doExport = () => {
    exportXlsx({
      filename: `stock_summary_${new Date().toISOString().slice(0,10)}.xlsx`,
      sheetName: "Stock Summary",
      columns: [
        { key: "sku",         label: "SKU" },
        { key: "name",        label: "Item" },
        { key: "category",    label: "Category" },
        { key: "unit",        label: "Unit" },
        { key: "total_qty",   label: "Total Qty" },
        { key: "cost_price",  label: "Cost Price ₹" },
        { key: "total_value", label: `Total Value ₹ (${data.valuation_method})` },
        { key: "warehouses_str", label: "Per Warehouse" },
      ],
      rows: rows.map((r) => ({
        ...r,
        warehouses_str: (r.per_warehouse || []).map((w) => `${w.warehouse_name}: ${w.qty}`).join(" · "),
      })),
    });
  };

  return (
    <div className="space-y-3" data-testid="stock-summary-tab">
      <div className="card-sharp p-3">
        <div className="grid md:grid-cols-6 gap-2 items-end">
          <div><Label className="text-[10px]">Warehouse</Label>
            <Select value={wh || "all"} onValueChange={(v) => setWh(v === "all" ? "" : v)}>
              <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="summary-wh-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All warehouses</SelectItem>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-[10px]">Item</Label>
            <Select value={item_id || "all"} onValueChange={(v) => setItem(v === "all" ? "" : v)}>
              <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="summary-item-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72"><SelectItem value="all">All items</SelectItem>{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-[10px]">Category</Label>
            <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
              <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="summary-cat-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-[10px]">SKU / name</Label>
            <div className="flex gap-1"><Input value={sku_q} onChange={(e) => setSkuQ(e.target.value)} className="rounded-sm h-8 text-xs" placeholder="search" data-testid="summary-sku-filter" />
            <Button size="sm" variant="outline" onClick={load} className="rounded-sm h-8">Go</Button></div></div>
          <div><Label className="text-[10px]">From</Label>
            <Input type="date" value={from_date} onChange={(e) => setFrom(e.target.value)} className="rounded-sm h-8 text-xs" data-testid="summary-from-filter" /></div>
          <div><Label className="text-[10px]">To</Label>
            <Input type="date" value={to_date} onChange={(e) => setTo(e.target.value)} className="rounded-sm h-8 text-xs" data-testid="summary-to-filter" /></div>
        </div>
        <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground border-t pt-2">
          <div>
            {rows.length} items · Qty <span className="font-mono">{fmtNum(data.total_qty || 0)}</span> · Value <span className="font-mono">{fmtINR(data.total_value || 0)}</span>
            {" · "}<span className="text-[10px] font-mono uppercase text-accent">{(data.valuation_method || "weighted_average").replaceAll("_", " ")}</span>
          </div>
          <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={doExport} data-testid="summary-export-xlsx">
            <Download className="w-3.5 h-3.5" /> Export XLSX
          </Button>
        </div>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>SKU</th><th>Item</th><th>Category</th><th className="text-right">Total Qty</th><th className="text-right">Value ({(data.valuation_method || "wa").split("_")[0].toUpperCase()})</th><th>Per Warehouse</th></tr></thead>
          <tbody data-testid="stock-summary-body">
            {rows.length === 0 && <tr><td colSpan="6" className="text-center text-muted-foreground py-8">No stock movements match the filter</td></tr>}
            {rows.map((r) => (
              <tr key={r.item_id} data-testid={`stock-row-${r.sku}`}>
                <td className="font-mono text-xs">{r.sku}</td>
                <td className="font-medium">{r.name}</td>
                <td className="text-[10px] font-mono uppercase text-muted-foreground">{r.category || "—"}</td>
                <td className="text-right tabular-nums font-medium">{fmtNum(r.total_qty)} <span className="text-[10px] text-muted-foreground">{r.unit}</span></td>
                <td className="text-right tabular-nums">{fmtINR(r.total_value)}</td>
                <td className="text-xs text-muted-foreground">
                  {(r.per_warehouse || []).map((w, i) => (
                    <span key={i} className="inline-block mr-2">
                      <b className="text-foreground">{w.warehouse_name}</b>: <span className="font-mono">{fmtNum(w.qty)}</span>
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Inventory() {
  const servicesOnly = new URLSearchParams(useLocation().search).get("mode") === "services";
  if (servicesOnly) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="items-services-page">
        <PageHeader eyebrow="Masters" title="Items & Services"
          description="Services you sell and goods / services you buy — SAC/HSN, GST, prices and default income / expense accounts. Pick them on sales invoices and purchase bills." />
        <ItemsTab servicesOnly />
      </div>
    );
  }
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="inventory-page">
      <PageHeader
        eyebrow="Operations"
        title="Inventory"
        description="Items · HSN · GST · warehouse → zone → bin hierarchy · stock movements. Item v2 supports RM / WIP / FG, multi-UOM and safety stock."
      />
      <Tabs defaultValue="items">
        <TabsList className="rounded-sm">
          <TabsTrigger value="items" className="rounded-sm" data-testid="tab-items">Items</TabsTrigger>
          <TabsTrigger value="warehouses" className="rounded-sm" data-testid="tab-warehouses">Warehouses</TabsTrigger>
          <TabsTrigger value="transfers" className="rounded-sm" data-testid="tab-transfers">Transfers</TabsTrigger>
          <TabsTrigger value="summary" className="rounded-sm" data-testid="tab-summary">Stock Summary</TabsTrigger>
          <TabsTrigger value="movements" className="rounded-sm" data-testid="tab-movements">Movements</TabsTrigger>
        </TabsList>
        <TabsContent value="items" className="mt-4"><ItemsTab /></TabsContent>
        <TabsContent value="warehouses" className="mt-4"><WarehouseTree /></TabsContent>
        <TabsContent value="transfers" className="mt-4"><TransfersTab /></TabsContent>
        <TabsContent value="summary" className="mt-4"><StockSummaryTab /></TabsContent>
        <TabsContent value="movements" className="mt-4"><MovementsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
